import { FormEvent, useEffect, useRef, useState } from "react";
import { pharmacyInventoryApi } from "@/core/inventory/inventory-api";
import type { StockProduct } from "@/core/pharmacy/types";
import { persistProductPrice } from "@/core/inventory/pharmacy-stock-sync";

interface QuickStockEntryModalProps {
  open: boolean;
  barcode: string;
  onClose: () => void;
  onCreated: (product: StockProduct, price: number, qty: number) => void;
}

export function QuickStockEntryModal({
  open,
  barcode,
  onClose,
  onCreated,
}: QuickStockEntryModalProps) {
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [qty, setQty] = useState("1");
  const [unitId, setUnitId] = useState("");
  const [storeId, setStoreId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setName("");
    setPrice("");
    setQty("1");
    setError(null);
    void pharmacyInventoryApi.listUnits().then((units) => {
      const active = units.find((u) => u.isActive);
      if (active) setUnitId(active.id);
    });
    void pharmacyInventoryApi.listStores().then((stores) => {
      if (stores[0]) setStoreId(stores[0].id);
    });
    window.setTimeout(() => nameRef.current?.focus(), 50);
  }, [open, barcode]);

  if (!open) return null;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const priceNum = Number(price);
    const qtyNum = Number(qty);
    if (!name.trim() || !unitId || priceNum <= 0 || qtyNum <= 0) {
      setError("Nom, prix et quantité requis.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const code = barcode.replace(/[^a-zA-Z0-9-]/g, "").slice(0, 40) || `P${Date.now().toString(36)}`;
      const product = await pharmacyInventoryApi.createProduct({
        code: code.toUpperCase(),
        sku: code.toUpperCase(),
        name: name.trim(),
        barcode: barcode.trim() || undefined,
        unitId,
        sellingPrice: priceNum,
      });
      if (storeId) {
        await pharmacyInventoryApi.postReceipt(product.id, storeId, qtyNum);
      }
      await persistProductPrice(product.id, priceNum);
      onCreated(product, priceNum, qtyNum);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Création impossible");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ph-modal-backdrop">
      <div className="ph-modal-card">
        <h2 className="h5 mb-1">Entrée en stock</h2>
        <p className="text-muted small mb-3">
          Produit inconnu — enregistrez-le puis il sera ajouté à la facture.
        </p>
        {barcode && (
          <p className="small mb-2">
            Code scanné : <code>{barcode}</code>
          </p>
        )}
        <form onSubmit={handleSubmit}>
          <div className="mb-2">
            <label className="form-label">Nom du produit</label>
            <input
              ref={nameRef}
              className="form-control"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="row g-2 mb-2">
            <div className="col-6">
              <label className="form-label">Prix unitaire</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="form-control"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                required
              />
            </div>
            <div className="col-6">
              <label className="form-label">Quantité entrée</label>
              <input
                type="number"
                min="1"
                className="form-control"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                required
              />
            </div>
          </div>
          {error && <div className="alert alert-danger py-2 small">{error}</div>}
          <div className="d-flex gap-2 justify-content-end mt-3">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
              Annuler
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Enregistrement…" : "Entrer en stock & ajouter"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
